import {
    builderObject,
    favoritesLeftNavItemId,
    makeHubPersonaId,
    createMapOfSet,
    hashFolderId,
    PersonType,
} from '../../../../shared';
import type { NavigationState, ClickedHubPersonaState } from '../../../state/view/navigationState';
import {
    personaClickedAction,
    personaFocusAction,
    personaShiftClickedAction,
    personaListFilterChangedAction,
    confirmDeletePersonasClickedAction,
    checkPersonaListItemToggledAction,
    allPersonasCheckedAction,
    checkedPersonasClearedAction,
    cancelMultiSelectClickedAction,
    linkPersonasCompletedAction,
    unlinkContactCompletedAction,
    contactEditorNewContactSaveClickedAction,
    contactEditorExistingContactSaveClickedAction,
    removeFavoritePersonaClickedAction,
    personaEditClickedAction,
    personaListScrollToGroupClickedAction,
    loadPersonasCompletedAction,
    moveContactsClickedAction,
    hardDeleteCompletedAction,
    restoreContactsCompletedAction,
    deletedItemsSubViewToggleClickedAction,
    mouseOverHubPersonaAction,
    mouseLeaveHubPersonaAction,
    getPersonaCompletedAction,
} from '../../../../hub';
import { menuEntryClickedAction, confirmDeleteFolderClickedAction } from '../../../../appchrome';
import { privateDLCreatedAction, privateDLEditedAction } from '../../../../privatedlcreateedit';
import { getClickedPersonaAfterDeleteOrMove } from '../../../utils/navigationReducerHelper';
import { importContactsUploadFileCompletedAction } from '../../../../importcontacts/actions';
import { getStateAfterFavoriteRemoved } from './../utils/navigationReducerUtils';
import { lpcExpandedViewNavigationAction } from '../../../actions/lpcActions';

export default (reducer: builderObject<NavigationState>): builderObject<NavigationState> =>
    reducer
        .handle(menuEntryClickedAction, (state, action) => {
            return {
                ...state,
                clickedLeftNavItemId: action.payload.folderId,
                clickedHubPersona: undefined,
                lastClickedHubPersonaBeforeEVNavigation: undefined,
                personaListCheckedHubPersonaDetails: [],
                currentQuery: undefined,
                clickedRightViewSection: undefined,
            };
        })
        .handle(confirmDeleteFolderClickedAction, (state, action) => {
            if (action.payload.folderId === state.clickedLeftNavItemId) {
                return {
                    ...state,
                    clickedLeftNavItemId: undefined,
                    clickedHubPersona: undefined,
                    personaListCheckedHubPersonaDetails: [],
                };
            }

            return state;
        })
        .handle(personaListScrollToGroupClickedAction, (state, action) => {
            return {
                ...state,
                clickedHubPersona: {
                    hubPersonaId: action.payload.firstHubPersonaIdInGroup,
                    origin: 'personaList',
                },
            };
        })
        // personaFocusAction is similar to clicked persona, but does not unset a selection of selected personas.
        // Used when moving focus using keyboard in hublist
        .handle(personaFocusAction, (state, action) => {
            const origin = state.clickedHubPersona?.origin || 'personaList';
            const focusPersona: ClickedHubPersonaState = {
                hubPersonaId: action.payload.hubPersonaId,
                origin: origin,
            };

            return {
                ...state,
                clickedHubPersona: focusPersona,
                lastClickedHubPersonaBeforeEVNavigation: focusPersona,
            };
        })
        .handle(personaClickedAction, (state, action) => {
            const clickedPersona: ClickedHubPersonaState = {
                hubPersonaId: action.payload.hubPersonaId,
                origin: action.payload.origin,
            };

            if (
                state.clickedHubPersona &&
                state.clickedHubPersona.hubPersonaId === action.payload.hubPersonaId
            ) {
                clickedPersona.origin = state.clickedHubPersona.origin;
            }

            return {
                ...state,
                personaListCheckedHubPersonaDetails: [],
                clickedHubPersona: clickedPersona,
                lastClickedHubPersonaBeforeEVNavigation: clickedPersona,
                firstClickedHubPersona: clickedPersona,
            };
        })
        .handle(personaShiftClickedAction, (state, action) => {
            const clickedPersona: ClickedHubPersonaState = {
                hubPersonaId: action.payload.hubPersonaId,
                origin: action.payload.origin,
            };

            if (
                state.clickedHubPersona &&
                state.clickedHubPersona.hubPersonaId === action.payload.hubPersonaId
            ) {
                clickedPersona.origin = state.clickedHubPersona.origin;
            }

            return {
                ...state,
                personaListCheckedHubPersonaDetails: [],
                clickedHubPersona: clickedPersona,
            };
        })
        .handle(personaEditClickedAction, (state, action) => {
            if (!state.clickedHubPersona) {
                const clickedPersona: ClickedHubPersonaState = {
                    hubPersonaId: action.payload.hubPersonaId,
                    origin: 'personaList',
                };
                return {
                    ...state,
                    personaListCheckedHubPersonaDetails: [],
                    clickedHubPersona: clickedPersona,
                };
            } else {
                return state;
            }
        })
        .handle(allPersonasCheckedAction, (state, action) => {
            const personaListCheckedHubPersonaDetails = action.payload.hubPersonaIds.map(id => ({
                hubPersonaId: id,
                dataFromGetPersona: false,
            }));
            return {
                ...state,
                personaListCheckedHubPersonaDetails: personaListCheckedHubPersonaDetails,
            };
        })
        .handle(checkedPersonasClearedAction, state => {
            return { ...state, personaListCheckedHubPersonaDetails: [] };
        })
        .handle(checkPersonaListItemToggledAction, (state, action) => {
            let clickedHubPersona = state.clickedHubPersona;

            const uncheckedMap = createMapOfSet(action.payload.uncheckedHubPersonaIds);
            const newCheckedHubPersonaIds = state.personaListCheckedHubPersonaDetails
                .filter(
                    checkedHubPersonaDetails => !uncheckedMap[checkedHubPersonaDetails.hubPersonaId]
                )
                .concat(
                    action.payload.checkedHubPersonaIds.map(checkedHubPersonaId => ({
                        hubPersonaId: checkedHubPersonaId,
                        dataFromGetPersona: false,
                    }))
                );

            if (newCheckedHubPersonaIds.length === 1) {
                clickedHubPersona = {
                    hubPersonaId: newCheckedHubPersonaIds[0].hubPersonaId,
                    origin: 'personaList',
                };
            }

            return {
                ...state,
                clickedHubPersona: clickedHubPersona,
                personaListCheckedHubPersonaDetails: newCheckedHubPersonaIds,
            };
        })
        .handle(privateDLCreatedAction, (state, action) => {
            /* User created a DL. In this case we want to display the DL in reading mode
             * and navigate to the folder of the created DL */
            return {
                ...state,
                clickedHubPersona: {
                    hubPersonaId: action.payload.hubPersonaId,
                    origin: 'personaList',
                },
                clickedLeftNavItemId: action.payload.folderId,
                personaListCheckedHubPersonaDetails: [],
                currentQuery: undefined,
                currentRoute: '',
            };
        })
        .handle(privateDLEditedAction, state => {
            return {
                ...state,
                personaListCheckedHubPersonaDetails: [],
                currentQuery: undefined,
            };
        })
        .handleDataAction(importContactsUploadFileCompletedAction, (state, action) => {
            return {
                ...state,
                clickedLeftNavItemId: action.payload.importFolderId,
                clickedHubPersona: undefined,
                personaListCheckedHubPersonaDetails: [],
            };
        })
        .handle(contactEditorNewContactSaveClickedAction, (state, { payload }) => {
            /* User created a new contact. In this case we want to display the new contact in reading mode
             * and navigate to the folder of the newly added contact. */
            return {
                ...state,
                clickedHubPersona: {
                    hubPersonaId:
                        payload.hubContact?.type === PersonType.Person
                            ? payload.hubContact.hubPersonaId
                            : makeHubPersonaId(payload.personaId),
                    origin: 'personaList',
                },
                clickedLeftNavItemId: payload.folderId,
                personaListCheckedHubPersonaDetails: [],
                currentQuery: undefined,
                currentRoute: '',
            };
        })
        .handle(contactEditorExistingContactSaveClickedAction, (state, { payload }) => {
            /* User modified an existing contact.
             * We do set the personaId as it may be result of auto-linking */
            if (payload.personaId) {
                return {
                    ...state,
                    clickedHubPersona: {
                        hubPersonaId: makeHubPersonaId(payload.personaId),
                        origin: state.clickedHubPersona
                            ? state.clickedHubPersona.origin
                            : 'personaList',
                    },
                };
            }

            return state;
        })
        .handle(confirmDeletePersonasClickedAction, (state, action) => {
            return {
                ...createNewStateAfterRemovingPersonasFromHubList(
                    state,
                    action.payload.displayedHubPersonaIds,
                    action.payload.deletedHubPersonaIds
                ),
                currentQuery:
                    state.currentQuery?.hubPersonaId &&
                    state.currentQuery.hubPersonaId === action.payload.deletedHubPersonaIds[0]
                        ? undefined
                        : state.currentQuery,
            };
        })
        .handle(removeFavoritePersonaClickedAction, (state, action) => {
            if (action.payload.activeLeftNavItemId === favoritesLeftNavItemId) {
                // We need to recalculate new selected persona when in favorites and removing item from favorites
                return getStateAfterFavoriteRemoved(
                    state,
                    action.payload.displayedHubPersonaIds,
                    action.payload.hubPersonaId
                );
            }

            return state;
        })
        .handle(personaListFilterChangedAction, state => {
            return {
                ...state,
                personaListCheckedHubPersonaDetails: [],
                clickedHubPersona: undefined,
            };
        })
        .handle(cancelMultiSelectClickedAction, state => {
            return { ...state, personaListCheckedHubPersonaDetails: [] };
        })
        .handleDataAction(linkPersonasCompletedAction, (state, action) => {
            return {
                ...state,
                personaListCheckedHubPersonaDetails: [],
                clickedHubPersona: {
                    hubPersonaId: action.payload.linkedPersona.hubPersonaId,
                    origin: state.clickedHubPersona
                        ? state.clickedHubPersona.origin
                        : 'personaList',
                },
            };
        })
        .handleDataAction(unlinkContactCompletedAction, (state, action) => {
            return {
                ...state,
                personaListCheckedHubPersonaDetails: [],
                clickedHubPersona: {
                    hubPersonaId: action.payload.updatedPersona.hubPersonaId,
                    origin: state.clickedHubPersona
                        ? state.clickedHubPersona.origin
                        : 'personaList',
                },
            };
        })
        .handleDataAction(loadPersonasCompletedAction, (state, action) => {
            if (
                // Do not clean up clicked personas if callback is from other than selected folder
                hashFolderId(state.clickedLeftNavItemId) !== action.payload.folderId ||
                !action.payload.isLastBatch ||
                (!state.personaListCheckedHubPersonaDetails.length && !state.clickedHubPersona) ||
                state.currentQuery
            ) {
                return state;
            }

            const mapOfHubPersonaIds = createMapOfSet(
                action.payload.personas.map(persona => persona.hubPersonaId)
            );

            return {
                ...state,
                personaListCheckedHubPersonaDetails: state.personaListCheckedHubPersonaDetails.filter(
                    checkedHubPersonaDetails =>
                        mapOfHubPersonaIds[checkedHubPersonaDetails.hubPersonaId]
                ),
                clickedHubPersona:
                    state.clickedHubPersona &&
                    mapOfHubPersonaIds[state.clickedHubPersona.hubPersonaId]
                        ? state.clickedHubPersona
                        : undefined,
            };
        })
        .handle(moveContactsClickedAction, (state, action) => {
            return createNewStateAfterRemovingPersonasFromHubList(
                state,
                action.payload.displayedHubPersonaIds,
                action.payload.personas.map(persona => persona.hubPersonaId)
            );
        })
        .handle(mouseOverHubPersonaAction, (state, action) => {
            return { ...state, mouseOverHubPersonaId: action.payload.hubPersonaId };
        })
        .handle(mouseLeaveHubPersonaAction, state => {
            return { ...state, mouseOverHubPersonaId: undefined };
        })
        .handleDataAction(lpcExpandedViewNavigationAction, (state, action) => {
            // If there is no more back navigation available, we are back on persona selected in the PH.
            const resetToCurrentHubPersonaBeforeEVNavigation = !action.payload
                .isNavigateBackAvailable;
            if (resetToCurrentHubPersonaBeforeEVNavigation) {
                return {
                    ...state,
                    currentRoute: '',
                    clickedHubPersona: state.firstClickedHubPersona,
                    isNavigateBackAvailable: false,
                };
            } else {
                return {
                    ...state,
                    currentRoute: '',
                    clickedHubPersona: {
                        hubPersonaId: action.payload.persona.hubPersonaId,
                        origin: 'PersonaExpandedView',
                    },
                    isNavigateBackAvailable: action.payload.isNavigateBackAvailable,
                };
            }
        })
        .handleDataAction(hardDeleteCompletedAction, (state, action) => {
            return createNewStateAfterRemovingPersonasFromHubList(
                state,
                action.payload.displayedHubPersonaIds,
                action.payload.personas.map(persona => persona.hubPersonaId)
            );
        })
        .handleDataAction(restoreContactsCompletedAction, (state, action) => {
            return createNewStateAfterRemovingPersonasFromHubList(
                state,
                action.payload.displayedHubPersonaIds,
                action.payload.personas.map(persona => persona.hubPersonaId)
            );
        })
        .handle(deletedItemsSubViewToggleClickedAction, state => {
            return {
                ...state,
                clickedHubPersona: null,
                personaListCheckedHubPersonaDetails: [],
            };
        })
        .handleDataAction(getPersonaCompletedAction, (state, action) => {
            return {
                ...state,
                personaListCheckedHubPersonaDetails: state.personaListCheckedHubPersonaDetails.map(
                    checkedHubPersonaDetails =>
                        checkedHubPersonaDetails.hubPersonaId ===
                        action.payload.persona.hubPersonaId
                            ? { ...checkedHubPersonaDetails, dataFromGetPersona: true }
                            : checkedHubPersonaDetails
                ),
            };
        });

const createNewStateAfterRemovingPersonasFromHubList = (
    state: NavigationState,
    displayedHubPersonaIds: string[],
    hubPersonaIds: string[]
): NavigationState => {
    const newClickedHubPersonaId = getClickedPersonaAfterDeleteOrMove(
        displayedHubPersonaIds,
        hubPersonaIds
    );

    const newClickedPersona = newClickedHubPersonaId
        ? {
              hubPersonaId: newClickedHubPersonaId,
              origin: state.clickedHubPersona ? state.clickedHubPersona.origin : 'personaList',
          }
        : undefined;

    return {
        ...state,
        clickedHubPersona: newClickedPersona,
        personaListCheckedHubPersonaDetails: [],
    };
};
